import { useCallback, useMemo, useState } from 'react';
import {
  ARTIST_PROFILE_SOURCES,
  profileVariantsOf,
  type ArtistProfileKind,
  type ArtistProfileResponse,
  type ArtistProfileSource,
} from 'fym-shared/src/music24/artistProfile';
import type { HeaderPreviewGround } from '../../studio/spotifyHeader/FrameGround';
import { useArtistProfile } from '../../studio/spotifyHeader/useArtistProfile';
import { useHeaderPictures } from '../../studio/spotifyHeader/useHeaderPictures';
import { profileFileGround } from '../profileAssets';
import { profileSlotOf } from '../profileSlots';
import type { ProfileBannerKind, ProfileImageTool } from '../profileRoute';
import { useProfileReleases } from '../useProfileReleases';
import { useProfileMake } from '../useProfileMake';
import {
  initialToolDraft,
  profileToolItems,
  toolDraftBlocker,
  toolKind,
  type ProfileToolDraft,
} from './profileToolDraft';
import { toolColours, toolGround } from './toolPreview';
import { initialBannerText, previewBannerFacts } from './bannerTextPreview';

/**
 * The state of one Profile tool (design b1–b4): the draft, the releases and pictures it picks
 * from, the grounds of the tiles and the preview, the colours, and "Make it". The draft lives in
 * memory; the screen remounts it for each tool (`key`).
 */
export function useProfileTool(input: {
  accountId: number;
  profile: ArtistProfileResponse;
  tool: ProfileImageTool;
  banner: ProfileBannerKind;
}) {
  const { accountId, profile, tool, banner } = input;
  const kind = toolKind(tool, banner);
  const slot = useMemo(() => profileSlotOf(profile.assets, kind, profileVariantsOf(kind)[0]), [kind, profile.assets]);
  const newestUuid = profile.newestRelease?.catalogItemUuid;
  const [draft, setDraft] = useState<ProfileToolDraft>(() =>
    initialToolDraft(kind, slot.current, newestUuid, initialBannerText(slot.current, profile))
  );
  const [madeAt, setMadeAt] = useState<number | null>(null);
  const change = useCallback((patch: Partial<ProfileToolDraft>) => {
    setMadeAt(null);
    setDraft((previous) => ({ ...previous, ...patch }));
  }, []);

  const { releases } = useProfileReleases(accountId);
  const release = releases.find((one) => one.catalogItemUuid === draft.catalogItemUuid);
  const aiRelease = release ?? releases[0];
  const artist = useArtistProfile(accountId);
  const pictures = useHeaderPictures({
    accountId,
    catalogItemUuid: aiRelease?.catalogItemUuid ?? null,
    themeId: profile.look.themeId,
  });
  const pictureUrl = useMemo(() => {
    const list = draft.source === 'bank' ? pictures.bank : draft.source === 'ai' ? pictures.ai : [];
    return list.find((picture) => picture.uuid === draft.pictureUuid)?.url;
  }, [draft.pictureUuid, draft.source, pictures.ai, pictures.bank]);

  const { colours, from } = useMemo(
    () => toolColours(kind, draft, profile.look, release),
    [draft, kind, profile.look, release]
  );
  // The banner text of the preview: the theme of the look and the facts the server will write.
  const releaseLook = release?.look ?? null;
  const themeId = from === 'release' && releaseLook !== null ? releaseLook.themeId : profile.look.themeId;
  const bannerPreview = useMemo(() => {
    const facts = previewBannerFacts(profile, draft, release);
    if (facts === null) {
      return null;
    }
    const coverRelease = releases.find((one) => one.catalogItemUuid === facts.facts.release?.catalogItemUuid);
    return { ...facts, variant: draft.bannerText, themeId, coverUrl: coverRelease?.artworkUrl ?? null };
  }, [draft, profile, release, releases, themeId]);
  const groundOf = useCallback(
    (one: ArtistProfileKind): HeaderPreviewGround => {
      const { current } = profileSlotOf(profile.assets, one, profileVariantsOf(one)[0]);
      const fallback = profileFileGround(current) ?? { kind: 'theme' };
      return toolGround(one, draft, { release, pictureUrl, fallback });
    },
    [draft, pictureUrl, profile.assets, release]
  );
  const tileGrounds = useMemo((): Readonly<Record<ArtistProfileSource, HeaderPreviewGround>> => {
    const still = (url: string | undefined): HeaderPreviewGround =>
      url === undefined ? { kind: 'theme' } : { kind: 'picture', url, focus: { x: 0.5, y: 0.4 } };
    const coverUrl = (release ?? releases[0])?.artworkUrl;
    const grounds = {} as Record<ArtistProfileSource, HeaderPreviewGround>;
    ARTIST_PROFILE_SOURCES.forEach((source) => {
      grounds[source] = { kind: 'theme' };
    });
    grounds.cover = toolGround(
      kind,
      { ...draft, source: 'cover' },
      { release: release ?? releases[0], pictureUrl: undefined, fallback: still(coverUrl) }
    );
    grounds.photo = still(draft.photoUrl ?? artist.photo?.url);
    grounds.bank = still(pictures.bank[0]?.thumbUrl);
    grounds.ai = still(pictures.ai[0]?.thumbUrl);
    return grounds;
  }, [artist.photo, draft, kind, pictures.ai, pictures.bank, release, releases]);

  // A bank or AI source with no pick yet takes the first picture, as the kit header step does.
  const pickSource = useCallback(
    (source: ArtistProfileSource) => {
      const patch: Partial<ProfileToolDraft> = { source, pictureUuid: undefined };
      if (source === 'bank') {
        patch.pictureUuid = pictures.bank[0]?.uuid;
      }
      if (source === 'ai') {
        patch.pictureUuid = pictures.ai[0]?.uuid;
      }
      if (source === 'photo' && draft.fileId === undefined && artist.photo !== null) {
        patch.fileId = artist.photo.fileId;
        patch.photoUrl = artist.photo.url;
      }
      change(patch);
    },
    [artist.photo, change, draft.fileId, pictures.ai, pictures.bank]
  );

  const make = useProfileMake(accountId);
  const blocker = toolDraftBlocker(draft);
  const submit = useCallback(() => {
    if (toolDraftBlocker(draft) !== null) {
      return;
    }
    make
      .make(profileToolItems(tool, banner, draft))
      .then((done) => setMadeAt(done ? Date.now() : null))
      .catch(console.error);
  }, [banner, draft, make, tool]);

  return {
    accountId,
    kind,
    slot,
    draft,
    change,
    pickSource,
    releases,
    release,
    aiRelease,
    artist,
    pictures,
    colours,
    coloursFrom: from,
    bannerPreview,
    groundOf,
    tileGrounds,
    make,
    blocker,
    submit,
    made: madeAt !== null,
  };
}
